import Modal from 'components/common/modal';
import Text from 'components/inputs/text';
import { ItemType } from 'data/types';
import { useSpaceTypeGroups } from 'hooks/use-space-type-groups';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

type SpaceTypePickerModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  areaSqft: number;
  onSelect: (itemId: number) => void;
  onCancel: () => void;
}>;

export default function SpaceTypePickerModal({
  openState,
  areaSqft,
  onSelect,
  onCancel
}: SpaceTypePickerModalProps) {
  const [open] = openState;
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLElement | null>(null);
  const { suggested, common, other, detached, isEmpty } =
    useSpaceTypeGroups(searchText);

  useEffect(() => {
    if (open) {
      setSearchText('');
    }
  }, [open]);

  const renderGroup = (title: string, items: ItemType[]) => {
    if (items.length === 0) {
      return null;
    }
    return (
      <div className='flex flex-col gap-2'>
        <div className='text-base font-semibold text-gray-900'>{title}</div>
        <div className='grid grid-cols-2 gap-2'>
          {items.map((item) => (
            <button
              key={item.id}
              type='button'
              className='rounded-md border-2 border-blue-300 bg-blue-100 px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500'
              onClick={() => onSelect(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      openState={openState}
      title='What type of space is this?'
      infoMode
      scrollableBody
      initialFocus={searchInputRef}
      onClose={onCancel}
    >
      <div className='flex flex-col gap-4'>
        <p className='text-center text-sm text-gray-600'>
          You drew a {areaSqft} sq ft space. Pick its type to add it to this
          floor.
        </p>
        <Text
          value={searchText}
          onInput={setSearchText}
          inputRef={searchInputRef}
          size='sm'
          placeholder='Search space types...'
        />
        {renderGroup('Your Spaces', suggested)}
        {renderGroup('Common Spaces', common)}
        {renderGroup('Other Spaces', other)}
        {renderGroup('Detached Spaces', detached)}
        {isEmpty && (
          <div className='text-center text-sm text-gray-500'>
            No spaces found
          </div>
        )}
      </div>
    </Modal>
  );
}
